/**
 * Taste Profile Insights Widget
 *
 * Displays taste profile summary on Dashboard and Discovery pages.
 * Shows profile strength and encourages detailed profile completion.
 */

import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Progress } from "../../components/ui/progress";
import { Badge } from "../../components/ui/badge";
import { Skeleton } from "../../components/ui/skeleton";
import { useAction, useQuery } from "wasp/client/operations";
import { ensureTasteProfile, getProfileInsightsEnvelope } from "wasp/client/operations";
import { AlertCircle, Edit2, Lightbulb } from "lucide-react";
import { useUser } from "../../../hooks/useUser";

export interface TasteProfileInsightsWidgetProps {
  userId?: string;
  variant?: "compact" | "full";
  showEditButton?: boolean;
  onEditClick?: () => void;
}

export const TasteProfileInsightsWidget: React.FC<TasteProfileInsightsWidgetProps> = ({
  variant = "full",
  showEditButton = true,
  onEditClick,
}) => {
  const { user } = useUser();
  // Ensure profile exists and is (idempotently) being built
  const doEnsure = useAction(ensureTasteProfile as any);
  React.useEffect(() => {
    doEnsure({}).catch(() => {});
  }, []);

  const { data: env, isLoading, error } = useQuery(
    getProfileInsightsEnvelope,
    {},
    {
      // Performance optimizations to prevent excessive refetching
      staleTime: 5 * 60 * 1000,       // Consider data fresh for 5 minutes
      gcTime: 10 * 60 * 1000,         // Keep unused data in cache for 10 minutes
      refetchOnWindowFocus: false,    // Don't refetch when window regains focus
      refetchOnMount: false,          // Don't refetch if data is already cached
      refetchOnReconnect: false,      // Don't refetch on network reconnect
    }
  );

  // Loading State
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Your Taste Profile</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-6 w-full" />
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-6 w-full" />
        </CardContent>
      </Card>
    );
  }

  // Pending/Failed/Idle State
  // If onboarding is complete, suppress the "not yet available" card to avoid noise.
  if (error || !env || env.status !== 'ready') {
    if (user?.onboardingComplete) {
      return null;
    }
    const msg = env?.status === 'pending'
      ? 'Building your taste profile…'
      : 'Complete the recipe pairing to see your personalized profile';
    return (
      <Card className="border-amber-200 bg-amber-50">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-amber-900">
                Profile not yet available
              </p>
              <p className="text-xs text-amber-700 mt-1">{msg}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Compact Variant
  if (variant === "compact") {
    const insights: any = (env as any).insights || {};
    return (
      <Card className="bg-gradient-to-br from-primary/5 to-transparent">
        <CardContent className="pt-4 pb-3">
          <div className="space-y-2">
            <p className="text-xs font-semibold text-muted-foreground uppercase">
              Your Taste
            </p>
            <p className="text-sm font-medium leading-relaxed line-clamp-2">
              {insights.widgetDisplay}
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Full Variant (Default)
  const insights: any = (env as any).insights || {};
  return (
    <Card className="bg-gradient-to-br from-primary/5 to-transparent border-primary/10">
      <CardHeader>
        <div className="flex items-start justify-between">
          <CardTitle className="text-base">Your Taste Profile</CardTitle>
          {showEditButton && (
            <Button
              onClick={onEditClick}
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
              title="Edit your profile"
            >
              <Edit2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Taste Summary */}
        <div className="space-y-3">
          <p className="text-sm font-medium text-foreground">
            {insights.widgetDisplay?.split("•")[0]}
          </p>

          {/* Display as badges */}
          <div className="flex flex-wrap gap-2">
            {insights.widgetDisplay?.split("•").map((item: string, idx: number) => (
              <Badge
                key={idx}
                variant={idx === 0 ? "default" : "secondary"}
                className={idx === 0 ? "hidden" : ""}
              >
                {item.trim()}
              </Badge>
            ))}
          </div>

          {/* Alternate display: all as badges */}
          {insights.widgetDisplay && (
            <div className="flex flex-wrap gap-2">
              {insights.widgetDisplay.split("•").map((item: string, idx: number) => (
                <Badge key={idx} variant="outline" className="text-xs">
                  {item.trim()}
                </Badge>
              ))}
            </div>
          )}
        </div>

        {/* Profile Strength Indicator */}
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-xs font-semibold text-muted-foreground">
              Profile Strength
            </span>
            <span className="text-sm font-bold text-primary">
              {insights.profileStrength}%
          </span>
        </div>
        <Progress value={insights.profileStrength} className="h-2" />
        </div>

        {/* Nudge Toward Detailed Profile */}
        {insights.profileStrength < 90 && (
          <div className="p-3 bg-blue-50 border border-blue-100 rounded-lg flex gap-3">
            <Lightbulb className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-blue-900">
              <p className="font-medium mb-1">Unlock even better recommendations</p>
              <p className="text-blue-800">
                Tell us about your cooking situation, dietary needs, and preferences.
              </p>
            </div>
          </div>
        )}

        {/* Edit Profile Button */}
        {showEditButton && (
          <Button
            onClick={onEditClick}
            variant="outline"
            size="sm"
            className="w-full"
          >
            ✏️ Edit Full Profile
          </Button>
        )}
      </CardContent>
    </Card>
  );
};
